import React, { useContext, useEffect, useState } from 'react'
import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import MicroModal from 'micromodal'
import { getExportHeaders } from '../../ReportingGroup/types/tableHeaders'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import { useParams } from 'react-router-dom'
import { ColumnDef, ColumnFiltersState, SortingState } from '@tanstack/react-table' 
import { TeamContext } from '../../MenuTabs/MenuTabs'
import useLicenseTable from '../context/LicenseTableContext'
import { LicensePresetsTableType, LicensePresetTableData } from './data/LicensePresetTableData'
import CreateLicensePreset from '../../../../../orgs/CreateLicensePreset'
import LicensesTableButton from '../Licenses/LicensesTableButton'
import { modalCreateLicensePresetId, modalManageLicensePresetId } from './data/Utils'
import Button from '../../../../common/button/Button'

const LicensePresetsTable = ({ licensePresetsData, refetchPresets }) => {

  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>([])
  const [ rowSelection, setRowSelection ] = React.useState({})
  const [ globalFilter, setGlobalFilter ] = React.useState('')
  const [ sorting, setSorting ] = React.useState<SortingState>([])
  const [ data, setData ] = React.useState( () => LicensePresetTableData( licensePresetsData ) )
  const { setUseHeader, refetchLicensePresets } = useLicenseTable()

  const params = useParams()
  const teamId = params.team_id ?? 0

  useEffect(() => {
    MicroModal.init()
    setUseHeader(['all'])
  }, [])

  const columns = React.useMemo<ColumnDef<LicensePresetsTableType>[]>(
    () => [
      {     
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'licensePresetID',
            accessorKey: 'licensePresetID',
            prettyName: 'License Preset ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>LICENSE PRESET ID</span>,
          },
          {
            id: 'title',
            accessorKey: 'title',
            prettyName: 'Title',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>TITLE</span>,
          },
          {
            id: 'licenses',
            accessorKey: 'licenses',
            prettyName: 'Licenses',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>LICENSES</span>,
          },
          {
            id: 'actions',
            accessorKey: 'actions',
            enableSorting: false,
            prettyName: 'Actions',
            cell: (info) => (
              <Actions info={ info.row.original } />
            ),
            footer: props => props.column.id,
            header: () => <span>ACTIONS</span>,
            meta: {
              className: "msync-custom-member-action-header",
            },
          },
        ]
      }
    ],
    []
  )

  const Actions = ({ info }) => {
    return (
      <div className='msync-table-action-column'>
        <CreateLicensePreset 
          presetID={ info.licensePresetID }
          type="edit"
          presetTitle={ info.title }
          selectedLicenses={ info.presetLicenses }
          orgID={ teamId }
          afterEdit={() => {
            refetchPresets()
          }}
        />
        <LicensesTableButton
          btnName='Manage'
          id={ modalManageLicensePresetId( info.licensePresetID ) }
          info={ info }
          teamType={'msynclicenses'}
          btnClassName="msync-button msync-button--is-size-sm msync-button--is-theme-secondary msync-button--is-style-outline"
        />
      </div>
    )
  }

  const exportHeaders = getExportHeaders('team-licenses')
  const { teamData } = useContext(TeamContext)

  return (
    <>
      <CreateLicensePreset
        type="add"
        orgID={ teamId }
        afterEdit={() => {
          refetchPresets()
        }}
      />
      <Button
        onClick={ () => MicroModal.show(modalCreateLicensePresetId) }
      >
        Create
      </Button>
      <TanstackContent
        data={ data }
        columns={ columns }
        rowSelection={ rowSelection }
        setRowSelection={ setRowSelection }
        sorting={ sorting }
        setSorting={ setSorting }
        globalFilter={ globalFilter }
        setGlobalFilter={ setGlobalFilter }
        exportHeaders={ exportHeaders }
        from='License presets'
        context='LicensePresets'
        hideFilters={ true }
        columnFilters={ columnFilters }
        setColumnFilters={ setColumnFilters }
      >
        <></>
      </TanstackContent>
    </>
  )
}

export default LicensePresetsTable
